import { infiniteQueryOptions, keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useGraphQLClient } from "../graphql/client";
import {
  collectionHandleQuery,
  collectionProductsQuery,
  collectionQuickAddQuery,
  collectionsFiltersQuery,
  collectionsQuery,
  collectionsTitleQuery,
} from "../graphql/collection-gql";
import { INFINITE_LIST_QUERY_OPTIONS, NO_CACHE_QUERY_OPTIONS, useCacheFirstRequest } from "../graphql/queryClient";
import { locale } from "../locales";
import type {
  ICollectionHandleQuery,
  ICollectionProductsQuery,
  ICollectionsFiltersQuery,
  ICollectionsQuery,
} from "../types/api/collection";
import { ToastType } from "../types/enum";
import type { ICollectionListParams, IUseCollectionQuery } from "../types/queries";

/** 의미 단위 파라미터 → GraphQL variables */
function buildCollectionVariables(params: ICollectionListParams | null) {
  if (!params?.handle) return null;

  return {
    handle: params.handle,
    first: params.first ?? 24,
    sortKey: params.sortKey ?? "COLLECTION_DEFAULT",
    reverse: params.reverse ?? false,
    filters: params.filters ?? [],
  };
}

function mergeCollectionPages<T extends { collection?: any }>(pages: T[] | undefined): T | undefined {
  if (!pages?.length) return undefined;

  const firstCollection = pages[0]?.collection;
  // collection이 없으면(잘못된 handle 등) 그대로 반환 → 호출부의 `!data.collection` 분기 유지
  if (!firstCollection) return pages[0];

  const lastCollection = pages[pages.length - 1].collection;

  return {
    collection: {
      ...lastCollection,
      products: {
        ...lastCollection.products,
        edges: pages.flatMap((page) => page.collection?.products?.edges ?? []),
        pageInfo: lastCollection.products.pageInfo,
      },
    },
  } as T;
}

/** 컬렉션 조회 (퀵애드/트렌딩/목록/필터/핸들/추천 상품) */
export default function useCollectionQuery({ lang, openToast, skipCollectionList, skipFilters, collection = null }: IUseCollectionQuery) {
  const request = useGraphQLClient();
  const cacheFirstRequest = useCacheFirstRequest();
  const queryClient = useQueryClient();

  /** 퀵 애드 컬렉션 조회. main의 cache-first 대응 (staleTime: Infinity → 마운트/인스턴스 간 캐시 공유) */
  const [quickAddVariables, setQuickAddVariables] = useState<Record<string, unknown> | null>(null);

  const quickAddResult = useQuery({
    queryKey: ["shopify", "collection-quickadd", quickAddVariables],
    queryFn: () => request<ICollectionHandleQuery>(collectionQuickAddQuery, quickAddVariables ?? {}),
    enabled: quickAddVariables !== null,
    staleTime: Infinity,
  });

  const fetchQuickAdd = (variables: Record<string, unknown>) => setQuickAddVariables(variables);

  /**
   * 컬렉션 handle 조회 (무한스크롤 + await 데이터). cache-first 복원을 위해 staleTime 부여.
   *
   * variables 는 두 경로로 정해진다.
   * - collection 파라미터: 훅이 GraphQL variables 를 조립한다. 첫 렌더부터 켜져 캐시를 동기적으로 읽고
   *   파라미터가 바뀌면 queryKey 가 바뀌어 자동 재조회된다. (라우트/URL 로 대상이 정해지는 목록 화면)
   * - fetchCollection(variables): 화면이 임의 handle 을 지목하는 경우. (퀵애드/홈배너/이벤트 등)
   */
  const [triggeredVariables, setTriggeredVariables] = useState<Record<string, unknown> | null>(null);

  // 호출부가 매 렌더 새 객체를 넘겨도 내용이 같으면 같은 결과를 쓰도록 내용으로 비교한다
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paramVariables = useMemo(() => buildCollectionVariables(collection), [JSON.stringify(collection ?? null)]);

  const collectionVariables = paramVariables ?? triggeredVariables;

  const collectionHandleOptions = (variables: Record<string, unknown> | null) =>
    infiniteQueryOptions({
      ...INFINITE_LIST_QUERY_OPTIONS,
      queryKey: ["shopify", "collection-handle", variables],
      queryFn: ({ pageParam }) => request<ICollectionHandleQuery>(collectionHandleQuery, { ...variables, after: pageParam }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        lastPage.collection?.products?.pageInfo?.hasNextPage ? (lastPage.collection.products.pageInfo.endCursor ?? undefined) : undefined,
    });

  const collectionInfinite = useInfiniteQuery({
    ...collectionHandleOptions(collectionVariables),
    enabled: collectionVariables !== null,
    // 필터/정렬이 바뀌어 queryKey 가 바뀌는 동안 이전 목록을 그대로 보여 준다.
    // collection 파라미터 경로에만 적용한다 — fetchCollection 을 쓰는 스토어의 로딩 UX 를 바꾸지 않기 위해.
    placeholderData: paramVariables ? keepPreviousData : undefined,
  });

  const collectionData = useMemo(() => mergeCollectionPages(collectionInfinite.data?.pages), [collectionInfinite.data]);

  /**
   * 조회 시작 + 첫 페이지 데이터 반환.
   * setState로 무한스크롤 observer를 구동하고, 동일 queryKey를 fetchInfiniteQuery로 채워
   * await 호출부(ProductDetailTab 등)가 즉시 데이터를 받도록 한다. (staleTime 덕에 중복 요청 없음)
   */
  const fetchCollection = async (variables: Record<string, unknown>) => {
    setTriggeredVariables(variables);
    const result = await queryClient.fetchInfiniteQuery(collectionHandleOptions(variables));
    return { data: mergeCollectionPages(result.pages) };
  };
  const fetchMoreCollection = () => collectionInfinite.fetchNextPage();
  const refetchCollection = async () => {
    const result = await collectionInfinite.refetch();
    return { data: mergeCollectionPages(result.data?.pages) };
  };

  /** 전체 컬렉션 목록 조회 (마운트 시 자동, 내부 전용) */
  const collectionListResult = useQuery({
    queryKey: ["shopify", "collection-list"],
    queryFn: () => request<ICollectionsQuery>(collectionsQuery),
    enabled: !skipCollectionList,
    staleTime: Infinity, // main의 기본 cache-first 대응 (마운트마다 재요청하지 않음)
  });

  /** 컬렉션 필터 조회 (마운트 시 자동, 내부 전용). no-cache → staleTime 0 */
  const collectionFiltersResult = useQuery({
    queryKey: ["shopify", "collection-filters", { first: 10 }],
    queryFn: () => request<ICollectionsFiltersQuery>(collectionsFiltersQuery, { first: 10 }),
    enabled: !skipFilters,
    ...NO_CACHE_QUERY_OPTIONS,
  });

  /** 전체 컬렉션 타이틀 조회 (명령형) */
  const allCollectionMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      cacheFirstRequest<ICollectionsQuery>("collections-title", collectionsTitleQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /**
   * 컬렉션의 상품만 가벼운 필드로 받는 조회 (무한스크롤).
   * 컬렉션 타이틀·필터·멤버십이 필요 없고 상품 수가 많은 화면(런치 목록)용이며, 화면이 handle 을 지목하므로 트리거로만 쓴다.
   */
  const [collectionProductsVariables, setCollectionProductsVariables] = useState<Record<string, unknown> | null>(null);

  const collectionProductsInfinite = useInfiniteQuery({
    ...INFINITE_LIST_QUERY_OPTIONS,
    queryKey: ["shopify", "collection-products", collectionProductsVariables],
    enabled: collectionProductsVariables !== null,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      request<ICollectionProductsQuery>(collectionProductsQuery, { ...collectionProductsVariables, after: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.collection?.products?.pageInfo?.hasNextPage ? (lastPage.collection.products.pageInfo.endCursor ?? undefined) : undefined,
  });

  const collectionProductsData = useMemo(
    () => mergeCollectionPages(collectionProductsInfinite.data?.pages),
    [collectionProductsInfinite.data],
  );

  const fetchCollectionProducts = (variables: Record<string, unknown>) => {
    setCollectionProductsVariables(variables);
  };

  const fetchMoreCollectionProducts = () => collectionProductsInfinite.fetchNextPage();

  // useQuery/useInfiniteQuery는 v5에서 onError가 없으므로 에러 토스트는 effect로 처리
  useEffect(() => {
    if (
      quickAddResult.error ||
      collectionInfinite.error ||
      collectionProductsInfinite.error ||
      collectionListResult.error ||
      collectionFiltersResult.error
    ) {
      openToast({ message: locale(lang).server, type: ToastType.Error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quickAddResult.error,
    collectionInfinite.error,
    collectionProductsInfinite.error,
    collectionListResult.error,
    collectionFiltersResult.error,
  ]);

  return {
    fetchQuickAdd,
    quickAddData: quickAddResult.data,
    quickAddLoading: quickAddResult.isFetching,
    collectionListData: collectionListResult.data,
    collectionListLoading: collectionListResult.isFetching,
    collectionFilters: collectionFiltersResult.data,
    fetchCollection,
    collectionData,
    collectionLoading: collectionInfinite.isFetching,
    fetchMoreCollection,
    refetchCollection,
    fetchAllCollection: allCollectionMutation.mutateAsync,
    allCollectionData: allCollectionMutation.data,
    allCollectionLoading: allCollectionMutation.isPending,
    fetchCollectionProducts,
    collectionProductsData,
    collectionProductsLoading: collectionProductsInfinite.isFetching,
    fetchMoreCollectionProducts,
  };
}
